import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useFeatures } from "src/config/featureManager";
import { FeatureKeysType, IActionTrackingData, ModuleMap, ModuleTypes } from "src/pages/Studies/models/interfaces";
import { updateUserActivityData } from "../states/userActivitySlice";

const EVENT_TYPE = "FEATURE_TIME_SPEND";

export const useScreenTracking = (screenName: string, data: IActionTrackingData = {}) => {
	const dispatch = useDispatch();
	const startTimeRef = useRef<number | null>(null);
	const hasInitializedRef = useRef<boolean>(false);
	const tabIdRef = useRef<string>(getTabId());
	const screenNameRef = useRef<string>(screenName);
	const { isFeatureAvailable } = useFeatures();
	const isUserActivityMonitoring = isFeatureAvailable(FeatureKeysType.UserActivityMonitoring);

	// Safely destructure with default values
	const { study_id = null, study_name = null, action_name = null } = data || {};

	useEffect(() => {
		screenNameRef.current = screenName;
	}, [screenName]);

	const addTrackingEvent = (eventData: any) => {
		dispatch(updateUserActivityData(eventData));
	};

	useEffect(() => {
		if (!isUserActivityMonitoring) {
			return;
		}
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;

		if (!document.hidden) {
			startTimeRef.current = Date.now();
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				if (startTimeRef.current) {
					addTrackingEvent({
						route: screenNameRef.current,
						module:
							action_name === ModuleTypes.Notes
								? ModuleMap.Notes
								: action_name === ModuleTypes.WebBoards
								? ModuleMap.WebBoards
								: action_name || screenNameRef.current,
						event_type: EVENT_TYPE,
						session_id: tabIdRef.current,
						description: `User exit ${screenNameRef.current} page ${study_name ? `of study: ${study_name}` : ""}`,
						start_time: new Date(startTimeRef.current).toISOString(),
						end_time: new Date().toISOString(),
						metadata: { study_id, study_name },
					});
					startTimeRef.current = null;
				}
			} else {
				startTimeRef.current = Date.now();
				tabIdRef.current = getTabId();
			}
		};

		const handleBeforeUnload = () => {
			if (!startTimeRef.current) return;
			addTrackingEvent({
				route: screenNameRef.current,
				module: action_name === ModuleTypes.Notes ? ModuleMap.Notes : action_name === ModuleTypes.WebBoards ? ModuleMap.WebBoards : action_name || screenNameRef.current,
				session_id: tabIdRef.current,
				description: `User exit ${screenNameRef.current} page ${study_name ? `of study: ${study_name}` : ""}`,
				event_type: EVENT_TYPE,
				start_time: new Date(startTimeRef.current).toISOString(),
				end_time: new Date().toISOString(),
				metadata: { study_id, study_name },
			});
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("beforeunload", handleBeforeUnload);

			if (startTimeRef.current) {
				addTrackingEvent({
					route: screenNameRef.current,
					module:
						action_name === ModuleTypes.Notes ? ModuleMap.Notes : action_name === ModuleTypes.WebBoards ? ModuleMap.WebBoards : action_name || screenNameRef.current,
					session_id: tabIdRef.current,
					description: `User exit ${screenNameRef.current} page ${study_name ? `of study: ${study_name}` : ""}`,
					event_type: EVENT_TYPE,
					start_time: new Date(startTimeRef.current).toISOString(),
					end_time: new Date().toISOString(),
					metadata: { study_id, study_name },
				});
			}
			hasInitializedRef.current = false;
		};
	}, []);
};

const getTabId = (): string => {
	return `tab_${Date.now()}_${crypto.randomUUID()}`;
};
